import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DataGateway } from '@/data/types';
import type { Branch, Membership, Task, TaskCategory } from '@/domain/types';
import type { Actor } from '@/services/org-service';
import { formatDate, formatDateTime, formatTime, relativeTime, todayInZone } from '@/lib/format';
import { cloneTaskToBranches, completeTask, isTaskOverdue, priorityLabel } from '@/services/task-service';
import { adaptTitleForBranch } from '@/services/template-service';
import { uploadAttachment } from '@/services/attachment-service';
import { applyPhotoWatermark } from '@/services/operations-hub-service';
import { UPLOAD_ACCEPT_ATTRIBUTE } from '@/domain/settings';
import {
  ApprovalBadge,
  Badge,
  Button,
  Modal,
  PriorityBadge,
  SelectInput,
  StatusBadge,
  TextArea,
  TextInput,
} from './ui';
import { Icon } from './Icons';

export interface TaskFilterState {
  status: string;
  priority: string;
  branchId: string;
  categoryId: string;
  assigneeMembershipId: string;
  search: string;
  from: string;
  to: string;
}

export const EMPTY_TASK_FILTERS: TaskFilterState = {
  status: '',
  priority: '',
  branchId: '',
  categoryId: '',
  assigneeMembershipId: '',
  search: '',
  from: '',
  to: '',
};

const TIME_SUFFIX_RE = /^(.+?)\s*[—–-]\s*([01]?\d|2[0-3]):([0-5]\d)\s*$/;

export interface TaskSeriesDayBucket {
  localDate: string;
  label: string;
  isToday: boolean;
  tasks: Task[];
}

export interface TaskSeriesGroup {
  key: string;
  baseTitle: string;
  tasks: Task[];
  activeTask: Task;
  dayBuckets: TaskSeriesDayBucket[];
  overdueCount: number;
}

export type GroupedTaskItem =
  | { kind: 'single'; key: string; task: Task }
  | { kind: 'series'; key: string; group: TaskSeriesGroup };

export function extractTaskSeriesMeta(
  task: Task,
  timeZone: string,
): {
  groupKey: string | null;
  baseTitle: string;
  slotLabel: string;
} {
  const trimmed = task.title.trim();
  const match = TIME_SUFFIX_RE.exec(trimmed);
  const baseTitle = match ? match[1]!.trim() : trimmed;
  const explicitSlot = match ? `${match[2]!.padStart(2, '0')}:${match[3]!}` : null;
  const slotLabel = explicitSlot || (task.startAt || task.dueAt ? formatTime(task.startAt || task.dueAt, timeZone) : task.localDate);

  if (task.templateId) {
    return {
      groupKey: `tpl:${task.templateId}:${task.branchId ?? ''}`,
      baseTitle,
      slotLabel,
    };
  }
  if (match) {
    return {
      groupKey: `series:${baseTitle}:${task.branchId ?? ''}:${task.categoryId}`,
      baseTitle,
      slotLabel,
    };
  }
  return {
    groupKey: null,
    baseTitle: trimmed,
    slotLabel,
  };
}

function pickActiveSeriesTask(tasks: Task[], today: string, nowMs: number): Task {
  const sorted = [...tasks].sort((a, b) => {
    const aTime = new Date(a.startAt || a.dueAt || a.createdAt).getTime();
    const bTime = new Date(b.startAt || b.dueAt || b.createdAt).getTime();
    return aTime - bTime;
  });

  const actionable = sorted.filter((t) => t.status !== 'completed' && t.status !== 'cancelled');
  const pool = actionable.length > 0 ? actionable : sorted;

  // 1) جولة نشطة حاليًا (بدأت ولم ينتهِ وقت استحقاقها بعد)
  const inWindow = pool.find((t) => {
    const startMs = new Date(t.startAt).getTime();
    const dueMs = t.dueAt ? new Date(t.dueAt).getTime() : startMs + 3_600_000;
    return startMs <= nowMs && nowMs <= dueMs;
  });
  if (inWindow) return inWindow;

  // 2) أقرب جولة قادمة في نفس اليوم الحالي
  const upcomingToday = pool.find((t) => {
    if (t.localDate !== today) return false;
    const dueMs = t.dueAt ? new Date(t.dueAt).getTime() : new Date(t.startAt).getTime();
    return dueMs >= nowMs;
  });
  if (upcomingToday) return upcomingToday;

  // 3) أي جولة في اليوم الحالي (الأحدث في اليوم)
  const todayTasks = pool.filter((t) => t.localDate === today);
  if (todayTasks.length > 0) return todayTasks[todayTasks.length - 1]!;

  // 4) أقرب جولة مستقبلية
  const upcomingAny = pool.find((t) => new Date(t.startAt).getTime() >= nowMs);
  if (upcomingAny) return upcomingAny;

  // 5) أحدث جولة في السلسلة
  return pool[pool.length - 1] ?? sorted[0]!;
}

export function groupTasksIntoItems(tasks: Task[], timeZone: string): GroupedTaskItem[] {
  const today = todayInZone(timeZone);
  const nowMs = Date.now();
  const buckets = new Map<string, { baseTitle: string; tasks: Task[] }>();
  const order: Array<{ type: 'single'; task: Task } | { type: 'bucket'; key: string }> = [];

  for (const task of tasks) {
    const meta = extractTaskSeriesMeta(task, timeZone);
    if (!meta.groupKey) {
      order.push({ type: 'single', task });
      continue;
    }
    const existing = buckets.get(meta.groupKey);
    if (existing) {
      existing.tasks.push(task);
    } else {
      buckets.set(meta.groupKey, { baseTitle: meta.baseTitle, tasks: [task] });
      order.push({ type: 'bucket', key: meta.groupKey });
    }
  }

  const result: GroupedTaskItem[] = [];
  for (const entry of order) {
    if (entry.type === 'single') {
      result.push({ kind: 'single', key: entry.task.id, task: entry.task });
      continue;
    }
    const bucket = buckets.get(entry.key);
    if (!bucket) continue;
    if (bucket.tasks.length === 1) {
      const onlyTask = bucket.tasks[0]!;
      result.push({ kind: 'single', key: onlyTask.id, task: onlyTask });
      continue;
    }

    const sortedTasks = [...bucket.tasks].sort((a, b) => {
      const aTime = new Date(a.startAt || a.dueAt || a.createdAt).getTime();
      const bTime = new Date(b.startAt || b.dueAt || b.createdAt).getTime();
      return aTime - bTime;
    });

    const activeTask = pickActiveSeriesTask(sortedTasks, today, nowMs);
    const byDate = new Map<string, Task[]>();
    for (const task of sortedTasks) {
      const dateKey = task.localDate || today;
      const list = byDate.get(dateKey);
      if (list) list.push(task);
      else byDate.set(dateKey, [task]);
    }

    const dayBuckets: TaskSeriesDayBucket[] = [...byDate.entries()]
      .sort(([dateA], [dateB]) => {
        if (dateA === today && dateB !== today) return -1;
        if (dateB === today && dateA !== today) return 1;
        return dateB.localeCompare(dateA);
      })
      .map(([localDate, dayTasks]) => ({
        localDate,
        label:
          localDate === today
            ? `اليوم (${formatDate(`${localDate}T12:00:00Z`, timeZone)})`
            : formatDate(`${localDate}T12:00:00Z`, timeZone),
        isToday: localDate === today,
        tasks: dayTasks,
      }));

    const overdueCount = sortedTasks.filter((t) => isTaskOverdue(t)).length;

    result.push({
      kind: 'series',
      key: entry.key,
      group: {
        key: entry.key,
        baseTitle: bucket.baseTitle,
        tasks: sortedTasks,
        activeTask,
        dayBuckets,
        overdueCount,
      },
    });
  }

  return result;
}

export function TaskSeriesCard({
  group,
  timeZone,
  onCloneTask,
  onDeleteSeries,
  onQuickComplete,
}: {
  group: TaskSeriesGroup;
  timeZone: string;
  onCloneTask?: (task: Task) => void;
  onDeleteSeries?: (group: TaskSeriesGroup) => void;
  onQuickComplete?: (task: Task) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { activeTask, dayBuckets, overdueCount } = group;
  const activeMeta = extractTaskSeriesMeta(activeTask, timeZone);
  const activeOverdue = isTaskOverdue(activeTask);

  // الجولات المعروضة بشكل مصغّر عند طي البطاقة: جولات اليوم (أو أحدث يوم) حول الجولة النشطة
  const primaryBucket = dayBuckets[0];
  const previewSlots = useMemo(() => {
    const source = primaryBucket?.tasks ?? group.tasks;
    if (source.length <= 6) return source;
    const activeIdx = Math.max(
      0,
      source.findIndex((t) => t.id === activeTask.id),
    );
    const start = Math.max(0, Math.min(activeIdx - 2, source.length - 6));
    return source.slice(start, start + 6);
  }, [activeTask.id, group.tasks, primaryBucket]);

  const hiddenCount = group.tasks.length - previewSlots.length;

  return (
    <div className={`task-card task-series-card ${activeOverdue ? 'overdue' : ''}`}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 6 }}>
        <PriorityBadge priority={activeTask.priority} />
        <span className="series-pill" title="مهمة دورية متكررة مجمّعة تلقائيًا">
          <Icon name="repeat" size={12} />
          <span>{group.tasks.length} جولة</span>
        </span>
      </div>

      <Link to={`/tasks/${activeTask.id}`} className="task-series-title-link">
        <div className="task-title">{group.baseTitle}</div>
      </Link>

      <Link
        to={`/tasks/${activeTask.id}`}
        className={`series-spotlight ${activeOverdue ? 'is-overdue' : ''}`}
        title="اضغط لفتح الجولة الحالية أو القادمة"
      >
        <div className="series-spotlight-main">
          <span className="series-spotlight-label">
            <Icon name="clock" size={12} /> الجولة الحالية / القادمة
          </span>
          <span className="series-spotlight-time">
            <bdi>{activeMeta.slotLabel}</bdi>
          </span>
        </div>
        <div className="series-spotlight-sub">
          <span>
            {activeTask.dueAt ? `الاستحقاق: ${formatDateTime(activeTask.dueAt, timeZone)}` : 'بدون موعد'}
          </span>
          <span className="series-open-link">فتح الجولة ←</span>
        </div>
      </Link>

      <div className="series-slots-section">
        <div className="series-slots-header">
          <span className="tiny muted">
            {primaryBucket?.isToday
              ? `مواعيد اليوم (${primaryBucket.tasks.length})`
              : `أحدث المواعيد (${primaryBucket?.label ?? ''})`}
          </span>
          {overdueCount > 0 ? (
            <span className="tiny danger-text strong">{overdueCount} متأخرة</span>
          ) : null}
        </div>

        {!expanded ? (
          <div className="series-slots-grid">
            {previewSlots.map((slotTask) => {
              const slotMeta = extractTaskSeriesMeta(slotTask, timeZone);
              const isCurrent = slotTask.id === activeTask.id;
              const isDone = slotTask.status === 'completed';
              const isSlotOverdue = isTaskOverdue(slotTask);
              return (
                <Link
                  key={slotTask.id}
                  to={`/tasks/${slotTask.id}`}
                  className={`series-slot-chip ${isCurrent ? 'is-current' : ''} ${
                    isDone ? 'is-done' : ''
                  } ${isSlotOverdue ? 'is-overdue' : ''}`}
                  title={`${slotTask.title} — ${
                    slotTask.dueAt ? formatDateTime(slotTask.dueAt, timeZone) : ''
                  }`}
                >
                  <span className="series-slot-dot" />
                  <bdi>{slotMeta.slotLabel}</bdi>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="series-days-expanded">
            {dayBuckets.map((bucket) => (
              <div key={bucket.localDate} className="series-day-group">
                <div className="series-day-title">
                  <span>{bucket.label}</span>
                  <span className="tiny muted">{bucket.tasks.length} جولة</span>
                </div>
                <div className="series-slots-grid">
                  {bucket.tasks.map((slotTask) => {
                    const slotMeta = extractTaskSeriesMeta(slotTask, timeZone);
                    const isCurrent = slotTask.id === activeTask.id;
                    const isDone = slotTask.status === 'completed';
                    const isSlotOverdue = isTaskOverdue(slotTask);
                    return (
                      <Link
                        key={slotTask.id}
                        to={`/tasks/${slotTask.id}`}
                        className={`series-slot-chip ${isCurrent ? 'is-current' : ''} ${
                          isDone ? 'is-done' : ''
                        } ${isSlotOverdue ? 'is-overdue' : ''}`}
                        title={`${slotTask.title} — ${
                          slotTask.dueAt ? formatDateTime(slotTask.dueAt, timeZone) : ''
                        }`}
                      >
                        <span className="series-slot-dot" />
                        <bdi>{slotMeta.slotLabel}</bdi>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {group.tasks.length > 6 || dayBuckets.length > 1 ? (
          <button
            type="button"
            className="series-expand-btn"
            onClick={() => setExpanded((prev) => !prev)}
          >
            <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={13} />
            <span>
              {expanded
                ? 'طيّ جدول المواعيد'
                : `عرض كل الجولات (${group.tasks.length})${hiddenCount > 0 ? ` · +${hiddenCount} أخرى` : ''}`}
            </span>
          </button>
        ) : null}
      </div>

      <div className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <StatusBadge status={activeTask.status} />
          <ApprovalBadge state={activeTask.approvalState} />
        </div>
        <div className="row" style={{ gap: 4 }}>
          {activeTask.requiresProof ? (
            <span className="tiny muted" title="تتطلب إثبات إنجاز">
              <Icon name="paperclip" size={13} />
            </span>
          ) : null}
          {onQuickComplete &&
          (activeTask.status === 'pending' ||
            activeTask.status === 'scheduled' ||
            activeTask.status === 'in_progress') ? (
            <button
              type="button"
              className="card-mini-btn"
              style={{
                background: 'var(--success-soft, rgba(22,163,74,0.14))',
                color: 'var(--success)',
                borderColor: 'var(--success)',
                fontWeight: 700,
              }}
              title="إنجاز سريع للجولة الحالية"
              onClick={() => onQuickComplete(activeTask)}
            >
              <Icon name="check" size={12} /> إنجاز سريع
            </button>
          ) : null}
          {onCloneTask ? (
            <button
              type="button"
              className="card-mini-btn"
              title="نسخ المهمة لفرع آخر"
              onClick={() => onCloneTask(activeTask)}
            >
              <Icon name="branch" size={12} /> نسخ لفرع
            </button>
          ) : null}
          {onDeleteSeries ? (
            <button
              type="button"
              className="card-mini-btn is-danger"
              title="حذف جولات هذه السلسلة"
              onClick={() => onDeleteSeries(group)}
            >
              <Icon name="trash" size={12} /> حذف
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function TaskCard({
  task,
  timeZone,
  assigneeCount,
  onCloneTask,
  onDeleteTask,
  onQuickComplete,
}: {
  task: Task;
  timeZone: string;
  assigneeCount?: number;
  onCloneTask?: (task: Task) => void;
  onDeleteTask?: (task: Task) => void;
  onQuickComplete?: (task: Task) => void;
}) {
  const overdue = isTaskOverdue(task);
  return (
    <div className={`task-card ${overdue ? 'overdue' : ''}`}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <PriorityBadge priority={task.priority} />
        {task.requiresProof ? (
          <span className="tiny muted" title="تتطلب إثبات إنجاز">
            <Icon name="paperclip" size={14} />
          </span>
        ) : null}
      </div>
      <Link to={`/tasks/${task.id}`} className="task-series-title-link">
        <div className="task-title">{task.title}</div>
      </Link>
      <div className="task-meta">
        <span className={overdue ? 'danger-text strong' : ''}>
          <Icon name="clock" size={13} /> {task.dueAt ? formatDateTime(task.dueAt, timeZone) : 'بدون موعد'}
        </span>
        {assigneeCount && assigneeCount > 1 ? (
          <span>
            <Icon name="users" size={13} /> {assigneeCount}
          </span>
        ) : null}
      </div>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <StatusBadge status={task.status} />
          <ApprovalBadge state={task.approvalState} />
          {task.isPersonal ? <Badge tone="muted">شخصية</Badge> : null}
        </div>
        {onQuickComplete || onCloneTask || onDeleteTask ? (
          <div className="row" style={{ gap: 4 }}>
            {onQuickComplete &&
            (task.status === 'pending' ||
              task.status === 'scheduled' ||
              task.status === 'in_progress') ? (
              <button
                type="button"
                className="card-mini-btn"
                style={{
                  background: 'var(--success-soft, rgba(22,163,74,0.14))',
                  color: 'var(--success)',
                  borderColor: 'var(--success)',
                  fontWeight: 700,
                }}
                title="إنجاز سريع للمهمة"
                onClick={() => onQuickComplete(task)}
              >
                <Icon name="check" size={12} /> إنجاز سريع
              </button>
            ) : null}
            {onCloneTask ? (
              <button
                type="button"
                className="card-mini-btn"
                title="نسخ المهمة لفرع آخر"
                onClick={() => onCloneTask(task)}
              >
                <Icon name="branch" size={12} /> نسخ لفرع
              </button>
            ) : null}
            {onDeleteTask ? (
              <button
                type="button"
                className="card-mini-btn is-danger"
                title="حذف المهمة"
                onClick={() => onDeleteTask(task)}
              >
                <Icon name="trash" size={12} /> حذف
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function TaskTable({
  tasks,
  timeZone,
  branchNames,
  memberNames,
  canDeleteTask,
  onDeleteTask,
  onDeleteSeries,
  onCloneTask,
  onQuickComplete,
}: {
  tasks: Task[];
  timeZone: string;
  branchNames: Map<string, string>;
  memberNames: Map<string, string>;
  canDeleteTask?: (task: Task) => boolean;
  onDeleteTask?: (task: Task) => void;
  onDeleteSeries?: (group: TaskSeriesGroup) => void;
  onCloneTask?: (task: Task) => void;
  onQuickComplete?: (task: Task) => void;
}) {
  const groupedItems = useMemo(() => groupTasksIntoItems(tasks, timeZone), [tasks, timeZone]);
  const [expandedSeriesKeys, setExpandedSeriesKeys] = useState<Record<string, boolean>>({});

  const toggleSeries = (key: string) => {
    setExpandedSeriesKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>المهمة</th>
            <th>الحالة</th>
            <th>الاعتماد</th>
            <th>الأولوية</th>
            <th>الفرع</th>
            <th>الاستحقاق</th>
            <th>المنشئ</th>
            <th aria-label="إجراءات" />
          </tr>
        </thead>
        <tbody>
          {groupedItems.map((item) => {
            if (item.kind === 'single') {
              const task = item.task;
              const overdue = isTaskOverdue(task);
              return (
                <tr key={task.id}>
                  <td>
                    <Link to={`/tasks/${task.id}`} className="strong">
                      {task.title}
                    </Link>
                    {task.isPersonal ? (
                      <span className="badge badge-muted" style={{ marginInlineStart: 6 }}>
                        شخصية
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <StatusBadge status={task.status} />
                  </td>
                  <td>
                    <ApprovalBadge state={task.approvalState} />
                  </td>
                  <td>{priorityLabel(task.priority)}</td>
                  <td className="small muted">
                    {task.branchId ? (branchNames.get(task.branchId) ?? '—') : '—'}
                  </td>
                  <td className={overdue ? 'danger-text strong small' : 'small'}>
                    {task.dueAt ? formatDateTime(task.dueAt, timeZone) : '—'}
                  </td>
                  <td className="small muted">
                    {task.createdByMembershipId
                      ? memberNames.get(task.createdByMembershipId) ?? 'عضوية محذوفة'
                      : task.createdByUserId
                        ? 'المنصة'
                        : '—'}
                  </td>
                  <td>
                    <div className="actions">
                      <Link to={`/tasks/${task.id}`} className="btn btn-sm btn-ghost" aria-label="عرض">
                        عرض
                      </Link>
                      {onQuickComplete &&
                      (task.status === 'pending' ||
                        task.status === 'scheduled' ||
                        task.status === 'in_progress') ? (
                        <button
                          type="button"
                          className="card-mini-btn"
                          style={{
                            background: 'var(--success-soft, rgba(22,163,74,0.14))',
                            color: 'var(--success)',
                            borderColor: 'var(--success)',
                            fontWeight: 700,
                          }}
                          title="إنجاز سريع للمهمة"
                          onClick={() => onQuickComplete(task)}
                        >
                          <Icon name="check" size={12} /> إنجاز سريع
                        </button>
                      ) : null}
                      {onCloneTask ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="branch"
                          aria-label="نسخ المهمة لفرع آخر"
                          onClick={() => onCloneTask(task)}
                        >
                          نسخ لفرع
                        </Button>
                      ) : null}
                      {onDeleteTask && (!canDeleteTask || canDeleteTask(task)) ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="trash"
                          aria-label="حذف المهمة"
                          onClick={() => onDeleteTask(task)}
                        >
                          حذف
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            }

            const { group } = item;
            const { activeTask } = group;
            const isExpanded = Boolean(expandedSeriesKeys[group.key]);
            const activeOverdue = isTaskOverdue(activeTask);
            const previewSlots = (group.dayBuckets[0]?.tasks ?? group.tasks).slice(0, 6);

            return (
              <Fragment key={group.key}>
                <tr className="series-table-row">
                  <td>
                    <div className="col" style={{ gap: 6 }}>
                      <div className="row wrap" style={{ gap: 8 }}>
                        <Link to={`/tasks/${activeTask.id}`} className="strong">
                          {group.baseTitle}
                        </Link>
                        <span className="series-pill">
                          <Icon name="repeat" size={11} />
                          <span>{group.tasks.length} جولة</span>
                        </span>
                        {group.overdueCount > 0 ? (
                          <span className="badge badge-danger">{group.overdueCount} متأخرة</span>
                        ) : null}
                      </div>
                      <div className="series-slots-grid" style={{ maxWidth: 460 }}>
                        {previewSlots.map((slotTask) => {
                          const slotMeta = extractTaskSeriesMeta(slotTask, timeZone);
                          const isCurrent = slotTask.id === activeTask.id;
                          const isDone = slotTask.status === 'completed';
                          const isSlotOverdue = isTaskOverdue(slotTask);
                          return (
                            <Link
                              key={slotTask.id}
                              to={`/tasks/${slotTask.id}`}
                              className={`series-slot-chip ${isCurrent ? 'is-current' : ''} ${
                                isDone ? 'is-done' : ''
                              } ${isSlotOverdue ? 'is-overdue' : ''}`}
                              title={`${slotTask.title} — ${
                                slotTask.dueAt ? formatDateTime(slotTask.dueAt, timeZone) : ''
                              }`}
                            >
                              <span className="series-slot-dot" />
                              <bdi>{slotMeta.slotLabel}</bdi>
                            </Link>
                          );
                        })}
                        <button
                          type="button"
                          className="series-inline-toggle"
                          onClick={() => toggleSeries(group.key)}
                        >
                          <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size={12} />
                          <span>{isExpanded ? 'طيّ' : `كل الجولات (${group.tasks.length})`}</span>
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={activeTask.status} />
                  </td>
                  <td>
                    <ApprovalBadge state={activeTask.approvalState} />
                  </td>
                  <td>{priorityLabel(activeTask.priority)}</td>
                  <td className="small muted">
                    {activeTask.branchId ? (branchNames.get(activeTask.branchId) ?? '—') : '—'}
                  </td>
                  <td className={activeOverdue ? 'danger-text strong small' : 'small'}>
                    <div>
                      {activeTask.dueAt ? formatDateTime(activeTask.dueAt, timeZone) : '—'}
                    </div>
                    <div className="tiny muted">الجولة الحالية / القادمة</div>
                  </td>
                  <td className="small muted">
                    {activeTask.createdByMembershipId
                      ? memberNames.get(activeTask.createdByMembershipId) ?? 'عضوية محذوفة'
                      : activeTask.createdByUserId
                        ? 'المنصة'
                        : '—'}
                  </td>
                  <td>
                    <div className="actions">
                      <Link
                        to={`/tasks/${activeTask.id}`}
                        className="btn btn-sm btn-primary"
                        aria-label="فتح الجولة الحالية"
                      >
                        الجولة الحالية
                      </Link>
                      {onQuickComplete &&
          (activeTask.status === 'pending' ||
            activeTask.status === 'scheduled' ||
            activeTask.status === 'in_progress') ? (
            <button
              type="button"
              className="card-mini-btn"
              style={{
                background: 'var(--success-soft, rgba(22,163,74,0.14))',
                color: 'var(--success)',
                borderColor: 'var(--success)',
                fontWeight: 700,
              }}
              title="إنجاز سريع للجولة الحالية"
              onClick={() => onQuickComplete(activeTask)}
            >
              <Icon name="check" size={12} /> إنجاز سريع
            </button>
          ) : null}
          {onCloneTask ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="branch"
                          aria-label="نسخ لفرع آخر"
                          onClick={() => onCloneTask(activeTask)}
                        >
                          نسخ لفرع
                        </Button>
                      ) : null}
                      {onDeleteSeries && (!canDeleteTask || canDeleteTask(activeTask)) ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="trash"
                          aria-label="حذف الجولات"
                          onClick={() => onDeleteSeries(group)}
                        >
                          حذف
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                        onClick={() => toggleSeries(group.key)}
                      >
                        {isExpanded ? 'طيّ' : 'التفاصيل'}
                      </Button>
                    </div>
                  </td>
                </tr>
                {isExpanded
                  ? group.tasks.map((subTask) => {
                      const subOverdue = isTaskOverdue(subTask);
                      const subMeta = extractTaskSeriesMeta(subTask, timeZone);
                      return (
                        <tr key={subTask.id} className="series-sub-row">
                          <td style={{ paddingInlineStart: 28 }}>
                            <Link to={`/tasks/${subTask.id}`} className="small strong">
                              <span className="muted">↳ جولة </span>
                              <bdi>{subMeta.slotLabel}</bdi>
                              <span className="muted"> · {formatDate(subTask.startAt, timeZone)}</span>
                            </Link>
                          </td>
                          <td>
                            <StatusBadge status={subTask.status} />
                          </td>
                          <td>
                            <ApprovalBadge state={subTask.approvalState} />
                          </td>
                          <td className="small">{priorityLabel(subTask.priority)}</td>
                          <td className="small muted">
                            {subTask.branchId ? (branchNames.get(subTask.branchId) ?? '—') : '—'}
                          </td>
                          <td className={subOverdue ? 'danger-text strong small' : 'small'}>
                            {subTask.dueAt ? formatDateTime(subTask.dueAt, timeZone) : '—'}
                          </td>
                          <td className="small muted">—</td>
                          <td>
                            <div className="actions">
                              <Link to={`/tasks/${subTask.id}`} className="btn btn-sm btn-ghost">
                                عرض
                              </Link>
                              {onQuickComplete &&
                              (subTask.status === 'pending' ||
                                subTask.status === 'scheduled' ||
                                subTask.status === 'in_progress') ? (
                                <button
                                  type="button"
                                  className="card-mini-btn"
                                  style={{
                                    background: 'var(--success-soft, rgba(22,163,74,0.14))',
                                    color: 'var(--success)',
                                    borderColor: 'var(--success)',
                                    fontWeight: 700,
                                  }}
                                  title="إنجاز سريع لهذه الجولة"
                                  onClick={() => onQuickComplete(subTask)}
                                >
                                  <Icon name="check" size={12} /> إنجاز سريع
                                </button>
                              ) : null}
                              {onCloneTask ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  icon="branch"
                                  onClick={() => onCloneTask(subTask)}
                                >
                                  نسخ
                                </Button>
                              ) : null}
                              {onDeleteTask && (!canDeleteTask || canDeleteTask(subTask)) ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  icon="trash"
                                  aria-label="حذف الجولة"
                                  onClick={() => onDeleteTask(subTask)}
                                >
                                  حذف
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function TaskFilterBar({
  value,
  onChange,
  branches,
  categories,
  members,
  showAssignee = true,
}: {
  value: TaskFilterState;
  onChange: (value: TaskFilterState) => void;
  branches: Branch[];
  categories: TaskCategory[];
  members: Membership[];
  showAssignee?: boolean;
}) {
  const set = (patch: Partial<TaskFilterState>) => onChange({ ...value, ...patch });
  return (
    <div className="filters">
      <SelectInput
        label="الحالة"
        value={value.status}
        onChange={(event) => set({ status: event.target.value })}
      >
        <option value="">كل الحالات</option>
        <option value="scheduled">مجدولة</option>
        <option value="pending">قيد الانتظار</option>
        <option value="in_progress">جارية</option>
        <option value="completed">مكتملة</option>
        <option value="cancelled">ملغاة</option>
      </SelectInput>
      <SelectInput
        label="الأولوية"
        value={value.priority}
        onChange={(event) => set({ priority: event.target.value })}
      >
        <option value="">كل الأولويات</option>
        <option value="urgent">عاجلة</option>
        <option value="high">عالية</option>
        <option value="normal">عادية</option>
        <option value="low">منخفضة</option>
      </SelectInput>
      <SelectInput
        label="الفرع"
        value={value.branchId}
        onChange={(event) => set({ branchId: event.target.value })}
      >
        <option value="">كل الفروع</option>
        {branches.map((branch) => (
          <option key={branch.id} value={branch.id}>
            {branch.name}
          </option>
        ))}
      </SelectInput>
      <SelectInput
        label="الفئة"
        value={value.categoryId}
        onChange={(event) => set({ categoryId: event.target.value })}
      >
        <option value="">كل الفئات</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </SelectInput>
      {showAssignee ? (
        <SelectInput
          label="المسؤول"
          containerClassName="filter-span-2"
          value={value.assigneeMembershipId}
          onChange={(event) => set({ assigneeMembershipId: event.target.value })}
        >
          <option value="">كل المسؤولين</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.jobTitle ?? member.employeeNo ?? member.id.slice(0, 8)}
            </option>
          ))}
        </SelectInput>
      ) : null}
      <TextInput
        label="من تاريخ"
        type="date"
        value={value.from}
        onChange={(event) => set({ from: event.target.value })}
      />
      <TextInput
        label="إلى تاريخ"
        type="date"
        value={value.to}
        onChange={(event) => set({ to: event.target.value })}
      />
    </div>
  );
}

export function TaskListSkeleton() {
  return (
    <div className="col" style={{ gap: 10 }}>
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          className="card"
          style={{ height: 56, opacity: 0.55 - index * 0.08 }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export function TaskDueText({ task, timeZone }: { task: Task; timeZone: string }) {
  if (!task.dueAt) return <span className="muted">بدون موعد</span>;
  return (
    <span className={isTaskOverdue(task) ? 'danger-text strong' : ''}>
      {formatDate(task.dueAt, timeZone)} · {relativeTime(task.dueAt)}
    </span>
  );
}

export function CloneTaskModal({
  task,
  gateway,
  actor,
  branches,
  members,
  branchNames,
  memberNames,
  onClose,
  onSuccess,
}: {
  task: Task | null;
  gateway: DataGateway;
  actor: { companyId: string; userId: string; membershipId: string | null } | null;
  branches: Branch[];
  members: Membership[];
  branchNames: Map<string, string>;
  memberNames: Map<string, string>;
  onClose: () => void;
  onSuccess: (createdTasks: Task[]) => void;
}) {
  const [title, setTitle] = useState('');
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);
  const [autoAdjustTitle, setAutoAdjustTitle] = useState(true);
  const [assigneeMode, setAssigneeMode] = useState<'branch_scope' | 'same_assignees' | 'custom'>(
    'branch_scope',
  );
  const [customAssigneeIds, setCustomAssigneeIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const branchBrandMap = useMemo(
    () => new Map(branches.map((b) => [b.id, b.brandId])),
    [branches],
  );

  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
    setError(null);
    const otherBranches = branches.filter((b) => b.id !== task.branchId);
    setSelectedBranchIds(
      otherBranches.length > 0
        ? [otherBranches[0]!.id]
        : task.branchId
          ? [task.branchId]
          : [],
    );
    setAutoAdjustTitle(true);
    setAssigneeMode('branch_scope');
    setCustomAssigneeIds(task.assigneeScope.membershipIds);
  }, [branches, task]);

  return (
    <Modal
      open={Boolean(task)}
      title="استنساخ / نسخ المهمة إلى فرع آخر"
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            إلغاء
          </Button>
          <Button
            variant="primary"
            icon="branch"
            loading={busy}
            onClick={async () => {
              if (!task || !actor) return;
              const targets: Array<string | null> =
                branches.length === 0 ? [null] : selectedBranchIds;
              if (targets.length === 0) {
                setError('اختر فرعًا واحدًا على الأقل للاستنساخ');
                return;
              }
              setBusy(true);
              setError(null);
              try {
                const created = await cloneTaskToBranches(gateway, actor, {
                  sourceTaskId: task.id,
                  targetBranchIds: targets,
                  customBaseTitle: title,
                  autoAdjustBranchName: autoAdjustTitle,
                  assigneeMode,
                  customAssigneeIds,
                  branchNames,
                  branchBrandMap,
                });
                onSuccess(created);
              } catch (cause) {
                setError(cause instanceof Error ? cause.message : 'تعذّر استنساخ المهمة');
              } finally {
                setBusy(false);
              }
            }}
          >
            نسخ المهمة ({selectedBranchIds.length || 1})
          </Button>
        </>
      }
    >
      {task ? (
        <div className="col" style={{ gap: 14 }}>
          {error ? <p className="danger-text small">{error}</p> : null}

          <TextInput
            label="عنوان المهمة"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />

          {branches.length > 0 ? (
            <div className="col" style={{ gap: 8 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="small strong">اختر الفرع أو الفروع المستهدفة</span>
                <div className="row" style={{ gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() =>
                      setSelectedBranchIds(
                        branches.filter((b) => b.id !== task.branchId).map((b) => b.id),
                      )
                    }
                  >
                    كل الفروع الأخرى
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => setSelectedBranchIds(branches.map((b) => b.id))}
                  >
                    كل الفروع
                  </button>
                </div>
              </div>

              <div className="branch-checklist">
                {branches.map((branch) => {
                  const checked = selectedBranchIds.includes(branch.id);
                  const isSameBranch = branch.id === task.branchId;
                  const previewTitle = adaptTitleForBranch({
                    baseTitle: title || task.title,
                    sourceBranchId: task.branchId,
                    targetBranchId: branch.id,
                    branchNames,
                    autoAdjustBranchName: autoAdjustTitle,
                  });
                  return (
                    <label
                      key={branch.id}
                      className={`choice-card ${checked ? 'is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedBranchIds((prev) => [...prev, branch.id]);
                          } else {
                            setSelectedBranchIds((prev) =>
                              prev.filter((id) => id !== branch.id),
                            );
                          }
                        }}
                      />
                      <div className="col" style={{ gap: 2, flex: 1 }}>
                        <div className="row" style={{ justifyContent: 'space-between' }}>
                          <span className="strong small">{branch.name}</span>
                          {isSameBranch ? (
                            <Badge tone="muted">الفرع الحالي للمهمة</Badge>
                          ) : null}
                        </div>
                        {checked ? (
                          <span className="tiny muted">العنوان الجديد: «{previewTitle}»</span>
                        ) : null}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}

          <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoAdjustTitle}
              onChange={(event) => setAutoAdjustTitle(event.target.checked)}
            />
            <span className="small">
              تحديث اسم الفرع داخل العنوان تلقائيًا لكل فرع مستهدف
            </span>
          </label>

          <SelectInput
            label="إسناد المسؤولين في الفرع الجديد"
            value={assigneeMode}
            onChange={(event) =>
              setAssigneeMode(
                event.target.value as 'branch_scope' | 'same_assignees' | 'custom',
              )
            }
          >
            <option value="branch_scope">
              تلقائيًا لموظفي الفرع المختار (نطاق الفرع)
            </option>
            <option value="same_assignees">نفس المسؤولين في المهمة الأصلية</option>
            <option value="custom">اختيار موظفين محددين يدويًا</option>
          </SelectInput>

          {assigneeMode === 'custom' ? (
            <div className="branch-checklist" style={{ maxHeight: 160 }}>
              {members.map((member) => {
                const checked = customAssigneeIds.includes(member.id);
                const name =
                  memberNames.get(member.id) ??
                  member.jobTitle ??
                  member.id.slice(0, 8);
                return (
                  <label
                    key={member.id}
                    className={`choice-card ${checked ? 'is-selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setCustomAssigneeIds((prev) => [...prev, member.id]);
                        } else {
                          setCustomAssigneeIds((prev) =>
                            prev.filter((id) => id !== member.id),
                          );
                        }
                      }}
                    />
                    <span className="small">{name}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

export function QuickCompleteTaskModal({
  task,
  gateway,
  actor,
  branchName,
  actorName,
  photoWatermarkEnabled,
  cameraOnlyProof,
  onClose,
  onSuccess,
}: {
  task: Task | null;
  gateway: DataGateway;
  actor: Actor | null;
  branchName: string;
  actorName: string;
  photoWatermarkEnabled: boolean;
  cameraOnlyProof: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [note, setNote] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNote('');
    setSelectedFile(null);
    setError(null);
  }, [task]);

  return (
    <Modal
      open={Boolean(task)}
      title={task ? `إنجاز سريع — ${task.title}` : 'إنجاز سريع'}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            إلغاء
          </Button>
          <Button
            variant="primary"
            icon="check"
            loading={busy}
            onClick={async () => {
              if (!task || !actor) return;
              if (task.requiresProof && !selectedFile && !note.trim()) {
                setError('هذه المهمة تتطلب إرفاق صورة إثبات أو كتابة ملاحظة توثيق.');
                return;
              }
              setBusy(true);
              setError(null);
              try {
                const attachmentIds: string[] = [];
                if (selectedFile) {
                  let processedFile = selectedFile;
                  if (photoWatermarkEnabled && selectedFile.type.startsWith('image/')) {
                    processedFile = await applyPhotoWatermark(selectedFile, {
                      companyName: 'TaskFlow',
                      branchName: branchName || 'الفرع',
                      employeeName: actorName || 'الموظف',
                    });
                  }
                  const uploaded = await uploadAttachment(gateway, actor, {
                    companyId: actor.companyId,
                    taskId: task.id,
                    kind: processedFile.type.startsWith('image/') ? 'image' : 'document',
                    file: processedFile,
                  });
                  attachmentIds.push(uploaded.id);
                }
                await completeTask(gateway, actor, {
                  taskId: task.id,
                  proofNote:
                    note.trim() ||
                    (selectedFile ? 'تم إرفاق إثبات الإنجاز عبر الإنجاز السريع' : null),
                  attachmentIds,
                });
                onSuccess();
              } catch (cause) {
                setError(cause instanceof Error ? cause.message : 'تعذّر إتمام المهمة');
              } finally {
                setBusy(false);
              }
            }}
          >
            إتمام المهمة الآن ✓
          </Button>
        </>
      }
    >
      {task ? (
        <div className="col" style={{ gap: 14 }}>
          {error ? <p className="danger-text small">{error}</p> : null}

          <div
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              background: 'var(--bg-muted)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="small strong">{task.title}</div>
            <div className="tiny muted">
              {task.requiresProof
                ? '⚠️ تتطلب هذه المهمة إرفاق صورة إثبات أو ملاحظة توثيق قبل الإنجاز.'
                : 'يمكنك إتمام هذه الجولة فورًا أو إرفاق صورة وملاحظة اختيارية.'}
            </div>
          </div>

          <div className="col" style={{ gap: 6 }}>
            <label className="small strong">
              {cameraOnlyProof
                ? '📷 التقاط صورة إثبات مباشرة بالكاميرا'
                : '📎 إرفاق صورة أو ملف إثبات'}
            </label>
            <input
              type="file"
              accept={cameraOnlyProof ? 'image/*' : UPLOAD_ACCEPT_ATTRIBUTE}
              capture={cameraOnlyProof ? 'environment' : undefined}
              onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
            />
            {photoWatermarkEnabled ? (
              <span className="tiny muted">
                🛡️ سيتم ختم الصورة تلقائيًا بالتاريخ والوقت واسم الفرع والموظف.
              </span>
            ) : null}
          </div>

          <TextArea
            label="ملاحظة الإنجاز (اختياري أو كإثبات نصي)"
            rows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="مثال: تم الفحص والتأكد من النظافة ودرجة الحرارة..."
          />
        </div>
      ) : null}
    </Modal>
  );
}

